/**
 * Map Layers API Service
 *
 * CRUD operations + file upload for custom map layers.
 * Follows notesService pattern: plain exported object with typed apiClient calls.
 */

import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type { UpdateLayerRequest } from '@/types/api/requests/layers';
import type {
  GetWorkspaceLayersResponse,
  UploadLayerResponse,
  GetLayerByIdResponse,
  UpdateLayerResponse,
} from '@/types/api/responses/layers';

export interface UploadLayerData {
  name: string;
  description?: string;
  styleJson?: string;
  featureCount: number;
  boundsJson?: string;
  file: File;
  geoJsonFile?: File;
}

export const layersService = {
  /** GET /api/v1/layers — list all layers in current workspace */
  getAll: () =>
    apiClient.get<GetWorkspaceLayersResponse>(API_ENDPOINTS.LAYERS.BASE),

  /** POST /api/v1/layers — upload a new layer (multipart/form-data) */
  upload: (data: UploadLayerData) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('featureCount', String(data.featureCount));
    formData.append('file', data.file);

    if (data.description) {
      formData.append('description', data.description);
    }
    if (data.styleJson) {
      formData.append('styleJson', data.styleJson);
    }
    if (data.boundsJson) {
      formData.append('boundsJson', data.boundsJson);
    }
    if (data.geoJsonFile) {
      formData.append('geoJsonFile', data.geoJsonFile);
    }

    return apiClient.post<UploadLayerResponse>(
      API_ENDPOINTS.LAYERS.BASE,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  /** GET /api/v1/layers/{id} — get layer details */
  getById: (id: string) =>
    apiClient.get<GetLayerByIdResponse>(API_ENDPOINTS.LAYERS.BY_ID(id)),

  /** PUT /api/v1/layers/{id} — update layer metadata */
  update: (id: string, data: UpdateLayerRequest) =>
    apiClient.put<UpdateLayerResponse>(API_ENDPOINTS.LAYERS.BY_ID(id), data),

  /** DELETE /api/v1/layers/{id} — delete layer and storage files */
  delete: (id: string) =>
    apiClient.delete<void>(API_ENDPOINTS.LAYERS.BY_ID(id)),

  /** GET /api/v1/layers/{id}/geojson — get signed URL for GeoJSON data (follows redirect) */
  getGeoJsonUrl: (id: string) => API_ENDPOINTS.LAYERS.GEOJSON(id),

  /** GET /api/v1/layers/{id}/download — get signed URL for original file download */
  getDownloadUrl: (id: string) => API_ENDPOINTS.LAYERS.DOWNLOAD(id),
};
